/**
 * ComparisonView Formatters
 *
 * Utility functions for formatting values and deltas in comparison views.
 */

import type { FieldMetadata } from '@/services/metadataService'

/**
 * Format numeric delta with + prefix for positive values.
 */
export function formatNumericDelta(delta: number, metadata?: FieldMetadata): string {
  const prefix = delta > 0 ? '+' : ''
  const pyType = (metadata?.py_type || 'string').toLowerCase()

  if (pyType === 'integer' || pyType === 'int') {
    return `${prefix}${Math.round(delta)}`
  }

  // Default to 2 decimal places for numeric types
  return `${prefix}${delta.toFixed(2)}`
}

/**
 * Format percentage delta for display.
 */
export function formatPercentDelta(percent: number): string {
  const prefix = percent > 0 ? '+' : ''
  return `${prefix}${percent.toFixed(1)}%`
}

/**
 * Get the field title from metadata.
 */
export function getFieldTitle(fieldKey: string, metadata?: FieldMetadata | { title?: string | null }): string {
  if (metadata?.title) {
    return metadata.title
  }

  // Convert camelCase to Title Case
  return fieldKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

/**
 * Format a value for text/clipboard display.
 */
export function formatValueForText(value: unknown, metadata?: FieldMetadata): string {
  if (value === null || value === undefined) {
    return '(empty)'
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (typeof value === 'number') {
    const pyType = metadata?.py_type?.toLowerCase() || ''
    if (pyType === 'numeric' || pyType === 'float') {
      // Format as currency if the value looks like money (2 decimal places)
      if (Number.isInteger(value * 100)) {
        return `$${value.toFixed(2)}`
      }
      return value.toFixed(2)
    }
    return String(value)
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return String(value)
}

/**
 * Format section name to title case display.
 */
export function formatSectionTitle(name: string): string {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Format record name for display.
 */
export function formatRecordName(record: Record<string, unknown> | null): string {
  if (!record) return 'No record'
  const name = record.name || record.title || record.label || record.uid
  return String(name ?? 'Unnamed')
}
